import React from 'react';
import PropTypes from 'prop-types';
import style from '@src/js/Style';
import Card from '@src/js/Components/Base/Card';
import Popup from '@src/js/Components/Base/Popup';
import Alarm from '@src/js/Components/Base/Alarm';
import Tooltip from '@src/js/Components/Base/Tooltip';
import Button from '@src/js/Components/Base/Button';
import storageManager from '@src/js/StorageManager';
import Input from '../Form/Input';
import Radio from '../Form/Radio';
import MortgageTrait from './MortgageTrait';
import form from '@src/styles/components/form/control.scss';
import NumberFormatter from '../../NumberFormatter';
import AdaptiveTrait from '../../AdaptiveTrait';
import ImageRange from '../Form/ImageRange';
import AmortizationSchedule from './AmortizationSchedule';
import AmortizationScheduleTable from './AmortizationScheduleTable';
import AmortizationScheduleResult from './AmortizationScheduleResult';
import TranslationManager from '@src/js/TranslationManager.js';
import EarlyPaymentForm from './EarlyPaymentForm';
import MortgageForm from './MortgageForm';
import SpoilerTags from '../Base/SpoilerTags';

const config = {
    debounceTimeout: 500,
};

export default class MortgageCost extends React.Component {

    constructor(props) {

        super(props);

        this.state = this.getDefaultStates();

        this.nodes = {
            form: null,
            alarm: null,
            details: null,
            chart: null,
            disclaimer: null,
        };

        new MortgageTrait(this);
        new AdaptiveTrait(this);

        this.formInputEvent = this.formInputEvent.bind(this);
        this.form = new MortgageForm({
            storageLink: this.props.from,
            currency: this.props.query.currency,
        });
    }

    getDefaultStates() {

        return {
            i18n: { year: null },
            price: null,
            initFee: null,
            term: null,
            rate: null,
            paymentType: null,
            details: null,
            application: null,
            amortization: null,
            popup: null,
            currency: null,
        };
    }

    static get propTypes() {

        return {
            from: PropTypes.string,
            query: PropTypes.object,
            onSubmit: PropTypes.func,
        };
    }

    async loadStateData() {

        const year = await TranslationManager.get('years');
        this.setState({ i18n: { year } });
        
        await this.form.load();

        return new Promise((resolve) => {

            const
                
                getFromStorageObject = (storage, id, throwError = true) => {

                    return storageManager.get(storage, id, true, throwError);
                };

            storageManager
                .getFrom(this.props.from)
                .then(() => {

                    const
                        countryId = 'App\\Entity\\Geo\\Country',
                        calculatorId = 'App\\Entity\\Service\\Calculator',
                        currency = this.props.query.currency,
                        state = {
            
                            price: this.form.state.price,
                
                            creditSum: this.form.state.creditSum,

                            initFee: this.form.state.initFee,
                
                            term: this.form.state.term,
                
                            rate: this.form.state.rate,
                
                            application: this.form.state.application,
                
                            paymentType: {
                                title: getFromStorageObject(countryId, 'mortgagePaymentTypeTitle').label,
                                tooltip: getFromStorageObject(countryId, 'mortgagePaymentTypeTitle').text,
                                annuityLabel: getFromStorageObject(countryId, 'mortgageTypeAnnuity').label,
                                differentiatedLabel: getFromStorageObject(countryId,
                                    'mortgageTypeDifferentiated').label,
                            },
                
                            details: {
                
                                sum: {
                                    label: storageManager.get(countryId, 'mortgageSumExtendedFieldLabel'),
                                    value: new NumberFormatter(0, currency),
                                },
                
                                sumWithoutOverpayment: {
                                    label: storageManager.get(countryId, 'mortgageLegendCreditBodyLabel'),
                                    value: new NumberFormatter(0, currency),
                                },

                                insurance: this.form.state.result.insurance,
                
                                sumOverpayment: {
                                    label: storageManager.get(countryId, 'mortgageLegendPercentLabel'),
                                    value: new NumberFormatter(0, currency),
                                },
                
                                other: this.form.state.result.other,
                
                                tax: this.form.state.result.tax,
                
                                commission: this.form.state.result.commission,

                                payment: {
                                    label: storageManager.get(countryId, 'mortgageChartPaymentsLabel'),
                                    value: new NumberFormatter(0, currency),
                                },

                                paymentWithoutOverpayment: {
                                    label: storageManager.get(countryId, 'mortgageLegendCreditBodyLabel'),
                                    value: new NumberFormatter(0, currency),
                                },

                                paymentPercent: {
                                    label: storageManager.get(countryId, 'mortgageLegendPercentLabel'),
                                    value: new NumberFormatter(0, currency),
                                },
                
                                sumPayment: {
                                    label: storageManager.get(countryId, 'mortgageLegendSumPaymentsLabel'),
                                    value: new NumberFormatter(0, currency),
                                },
                
                            },
                
                            amortization: {
                                button: storageManager.get(countryId, 'mortgageAmortizationButton', false, false),
                            },
                
                            popup: getFromStorageObject(calculatorId, 'mortagePopup'),
                
                            currency,
                    };

                    resolve(state);
                });

            });
    }

    componentDidMount() {

        try {

            this
                .loadStateData()
                .then((state) => this.setState(state));
        }
        catch(error) {

            console.error(error);
        }
    }

    componentDidUpdate() {

        if (this.nodes.form) {

            if (!this.isInputInit) {

                this.isInputInit = true;

                this.nodes.form
                    .querySelectorAll('[name]')
                    .forEach((input) => {

                        input.addEventListener('change', this.formInputEvent);
                    });

                this.submit();
            }
        }
    }

    //------------------------------------------------------------------------------------------------------------------
    // RENDER
    //------------------------------------------------------------------------------------------------------------------

    renderMobile() {

        if (Object.values(this.state).indexOf(null) > -1)
            return null;
        // not yet

        return (
            <form ref={ (node) => { this.nodes.form = node; } } onSubmitCapture={ (e) => this.submitEvent(e) }>

                <Alarm ref={ (node) => { this.nodes.alarm = node; } }/>

                <div className={ style('flex flex-wrap gap-lg') }>

                    {/* part 1: inputs */}
                    { this.renderPart1() }

                    {/* part 2: visualization */}
                    { this.renderPart2(true) }

                </div>
                
                { this.renderFooter() }

            </form>
        );
    }

    renderResultDetail(props, property = 'value') {

        if (props?.label == undefined || props[property] <= 0 || props[property] == undefined)
            return null;

        return (
            <div className={ style('flex flex-wrap') }>
                <div className={ style('text-small text-muted w-full flex items-center') }>
                    { props.label }
                    { props.tooltip ? <Tooltip className={ style('text-black') } children={ props.tooltip } /> : null }
                </div>
                <div className={ style('title4 inline-block mt-3 loader__child flex flex-wrap items-end') }>
                    { props[property].round().formatted() }
                </div>
            </div>
        );
    }

    renderDesktop() {

        if (Object.values(this.state).indexOf(null) > -1)
            return null;
        // not yet

        return (
            <form ref={ (node) => { this.nodes.form = node; } } onSubmitCapture={ (e) => this.submitEvent(e) }>

                <Card isDefault={ true }>

                    <Alarm ref={ (node) => { this.nodes.alarm = node; } }/>

                    <div className={ style('grid-2 gap-2xl w-full') }>

                        {/* part 1: inputs */}
                        <div className={ style('flex flex-wrap gap-lg') }>
                            { this.renderPart1() }
                        </div>

                        {/* part 2: visualization */}
                        <div className={ style('flex flex-wrap gap-lg') }>

                            { this.renderPart2(false) }

                        </div>

                    </div>

                    { this.renderFooter() }

                </Card>
            </form>
        );
    }

    renderAmortization() {

        if (this.state.table == undefined)
            return null;

        const
            
            rate = {
                label: this.state.rate.label,
                value: NumberFormatter.parse(this.state.rate.value, null, '%'),
            },
            percent = {
                label: this.state.details.sumOverpayment.label,
                value: NumberFormatter.parse(this.state.details.sumOverpayment.value, null, this.state.currency),
                newValue: NumberFormatter.parse(this.state.details.sumOverpayment.value, null, this.state.currency),
            },

            report = this.getReportQuery();

        let
            result = null;

        return (<>

            <EarlyPaymentForm
                query={ this.props.query }
                table={ this.state.table }
                form={ this.form }
                report={ report }
                ref={ (node) => { this.nodes.earlyPaymentForm = node; } }
                payment={ this.state.details.payment }
                currency={ this.state.currency }
                />
                
            <AmortizationScheduleResult
                table={ this.state.table }
                ref={ (node) => { result = node; } }
                />

            <AmortizationSchedule
                table={ this.state.table }
                report={ report }
                ref={ (node) => { this.nodes.amortization = node; } }
                sum={ this.state.details.sum }
                form={ this.form }
                term={ this.state.term }
                rate={ rate }
                currency={ this.state.currency }
                percent={ percent }
                payment={ this.state.payment }
                paymentWithoutOverpayment={ this.state.details.paymentWithoutOverpayment }
                paymentPercent={ this.state.details.paymentPercent }
                showResult={ () => result.show() }
                getEarlyPaymentForm={ () => this.nodes.earlyPaymentForm }
                />

        </>);
    }

    renderFooter() {

        return null;
    }

    renderImageRange() {

        return <ImageRange
            className={ style('size-y-210 object-contain') }
            value={ this.state.price.value.toFloat() }
            items={ this.state.price.images } />;
    }

    renderPart1() {

        return (<>

            {/* price */}
            { this.renderPrice() }
            
            {/* init fee */}
            <div className={ style('flex flex-wrap w-full') }>
                
                <label className={ style('title4 text-medium') }>{ this.state.initFee.label }</label>
                <div className={ style('flex w-full form-control p-0 mt-2', form) }>
                    <Input
                        name='initial_fee-value'
                        format='number'
                        suffix='%'
                        min={ this.state.initFee.min }
                        max={ this.state.initFee.max }
                        value={ this.state.initFee.value } />
                    <input type='hidden' name='initial_fee-unit' value='percent' />
                </div>
                { this.renderTags(this.state.initFee.tags, 'initial_fee-value', '%', this.state.initFee.value) }
            </div>
            
            {/* term */}
            { this.renderTerm() }
            
            {/* rate */}
            { this.renderRate() }
            
            {/* payment type */}
            { this.renderPaymentType() }

        </>);
    }

    renderPrice() {

        return (
            <div className={ style('flex flex-wrap w-full') }>
                <Input
                    onChange={ (e) => this.changeRangeEvent(e) }
                    label={ this.state.price.label }
                    name='price'
                    format='number'
                    min={ this.state.price.min }
                    max={ this.state.price.max }
                    suffix={ this.state.currency }
                    value={ this.state.price.value } />
                { this.renderTags(this.state.price.tags, 'price', this.state.currency, this.state.price.value) }
            </div>
        );
    }

    renderTerm() {
        
        return (
            <div className={ style('flex flex-wrap w-full') }>
                <Input
                    label={ this.state.term.label }
                    name='credit_term'
                    format='number'
                    min={ this.state.term.min }
                    max={ this.state.term.max }
                    value={ this.state.term.value } />
                { this.renderTags(this.state.term.tags, 'credit_term', this.form.i18n.years, this.state.term.value) }
            </div>
        );
    }

    renderRate() {

        return (
            <div className={ style('flex flex-wrap w-full') }>
                <Input
                    label={ this.state.rate.label }
                    name='credit_rate'
                    format='number'
                    suffix='%'
                    min={ this.state.rate.min }
                    max={ this.state.rate.max }
                    value={ this.state.rate.value } />
                {/* { this.renderTags(this.state.rate.tags, 'credit_rate') } */}
            </div>
        );
    }

    renderPaymentType() {
        
        return (
            <div className={ style('flex flex-wrap gap-md') }>
            
                <div className={ style('w-full flex items-center') }>
                    <label className={ style('title4') }>{ this.state.paymentType.title }</label>
                    <Tooltip className={ style('text-black') }>{ this.state.paymentType.tooltip }</Tooltip>
                </div>

                <div className={ style('flex mobile:flex-wrap gap') }>
                    <Radio
                        className={ style('mobile:w-full') }
                        text={ this.state.paymentType.annuityLabel }
                        name='credit_type'
                        value='ann'
                        checked={ true } />
                    <Radio
                        className={ style('mobile:w-full') }
                        text={ this.state.paymentType.differentiatedLabel }
                        name='credit_type'
                        value='diff' />
                </div>

            </div>
        );
    }

    renderPart2(isMobile) {

        return (<>

            { !isMobile ? this.renderImageRange() : null }

            {/* credit details */}
            <div className={ style('sticky-mobile bottom-0 w-full bg-white border-top desktop:border-none') }>
                <div
                    className={ style('py-4 grid-2 loader items-stretch') }
                    ref={ (node) => { this.nodes.details = node; } }>

                        {/* payment */}
                        { this.renderResultDetail(this.state.details.payment) }
        
                        {/* required salary */}
                        { this.renderResultDetail(this.state.details.requiredSalary, 'calculatedValue') }
        
                        {/* credit sum */}
                        { this.renderResultDetail(this.state.details.sumWithoutOverpayment) }
        
                        {/* tax deduction */}
                        { this.renderResultDetail(this.state.details.taxDeduction, 'calculatedValue') }
    
                </div>
            </div>
            
            { this.renderControls() }
        </>);
    }

    renderControls(renderAmortization = true, className = null) {

        let amortizationContent = null;
        if (renderAmortization) {


            amortizationContent = (<>

                { this.renderAmortization() }

                <Button className={ style('button-primary-outline w-full') } onClick={ () => this.amortizationEvent() }>
                    { this.state.amortization.button }
                </Button>

            </>);
        }

        const classes = [style('flex flex-wrap w-full gap justify-center')];
        if (className)
            classes.push(className);
        
        return (
            <div className={ classes.join(' ') }>
            
                {/* amortization schedule */}
                { amortizationContent }
                
                {/* application */}
                { this.renderApplicationButton() }

                {/* disclaimer */}
                <Popup title={ this.state.popup.label } ref={ (node) => { this.nodes.disclaimer = node; } }>
                    { this.state.popup.text }
                </Popup>
                <span
                    className={ style('text-primary cursor-pointer underline') }
                    onClick={ () => this.nodes.disclaimer.show() }>
                        { this.state.popup.label }
                </span>

            </div>
        );
    }

    renderApplicationButton() {

        if (this.state?.application?.button == undefined)
            return null;

        return (
            <Button className={ style('w-full') } onClick={ () => this.applicationClickEvent(this.state.application) }>
                { this.state.application.button }
            </Button>
        );
    }

    renderTags(tags, inputName, suffix = null, defaultValue = null) {

        const buttons = tags.map((tag, id) => {

            const
                classes = [style('button-secondary text-small text-normal px-4 py-2')],
                tagNumber = NumberFormatter.parseEx(tag.value, { currency: suffix });

            if (defaultValue !== null && tagNumber.eq(defaultValue))
                classes.push(style('active'));

            return (
                <Button
                    key={ id }
                    className={ classes.join(' ') }
                    data-role='tag'
                    onClick={ (e) => this.changeActiveButtonEvent(e, inputName) }
                    children={ tagNumber.formatted() }
                />
            );
        });
        
        return <SpoilerTags className={ style('mt-4') }>{ buttons }</SpoilerTags>;
    }

    //------------------------------------------------------------------------------------------------------------------
    // ACTIONS
    //------------------------------------------------------------------------------------------------------------------
    
    submit() {

        this.nodes.alarm.hideAll();
        this.setIsLoading(true);
        
        const query = this.updateReportQuery();

        query
            .fetch()
            .then((response) => {
                
                const details = this.state.details;

                this.nodes.form.response = response;
                this.form.handleResponse(query, response);
                
                details.payment.value = this.form.state.result.payment.value;
                details.sumWithoutOverpayment.value = this.form.state.result.creditBody.value;
                details.paymentPercent.value = this.form.state.result.paymentOverpayment.value;
                details.paymentWithoutOverpayment.value = this.form.state.result.paymentCreditBody.value;
                details.taxDeduction = this.form.state.result.taxDeduction;
                details.requiredSalary = this.form.state.result.requiredSalary;
                
                details.sum.value =
                    NumberFormatter.parse(response.report.details.sumPayment, null, this.state.currency);

                details.sumPayment.value =
                    NumberFormatter.parse(response.report.details.sumPayment, null, this.state.currency);

                this.state.term.newValue = response.report.details?.termNew;

                details.sumOverpayment.value =
                    NumberFormatter.parse(response.report.details.sumOverpayment, null, this.state.currency);

                details.sumOverpayment.newValue = response.report.details?.sumOverpaymentNew
                    ? NumberFormatter.parse(response.report.details.sumOverpaymentNew, null, this.state.currency)
                    : null;
                const plusToSumPayment = (name) => {

                    details.sumPayment.value.plus(NumberFormatter.parse(this.nodes.form
                        .querySelector(`[name="${name}"]`)
                        ?.value));
                };

                plusToSumPayment('insurance');
                plusToSumPayment('other');
                plusToSumPayment('tax');
                plusToSumPayment('commission');

                this.setState({
                    details,
                    table: new AmortizationScheduleTable(response),
                });

                if (this.props.onSubmit)
                    this.props.onSubmit(new CustomEvent('submit', { detail: {
                        target: this.nodes.form,
                        response,
                    } }));

                this.setIsLoading(false);
            })
            .catch((e) => {

                console.error(e);

                if (e.code == 500) {

                    this.nodes.alarm.show({
                        title: 'Server error',
                        message: 'An unknown server error has occurred. Please try again later.',
                    });
                }
                else {

                    this.nodes.alarm.show({
                        title: 'Error',
                        message: e.message,
                    });
                }
            });
    }

    setIsLoading(status) {

        if (this.nodes?.details) {

            const loadingClass = style('loading');
            status
                ? this.nodes.details.classList.add(loadingClass)
                : this.nodes.details.classList.remove(loadingClass)
        }
    }

    //------------------------------------------------------------------------------------------------------------------
    // EVENTS
    //------------------------------------------------------------------------------------------------------------------

    formInputEvent(e) {

        if (e.type == 'change') {

            const name = e.target.dataset.alias ?? e.target.name;
            if (this.state[name] != undefined)
                this.state[name].value = NumberFormatter.parse(e.target.value);
        }

        this.debounceSubmit();
    }

    debounceSubmit() {

        this.setIsLoading(true);

        if (this.interactionTimeoutId) {

            clearTimeout(this.interactionTimeoutId);
            this.interactionPromise.reject();
        }
        
        let refReject = null;
        const promise = new Promise((resolve) => {

            refReject = () => resolve(false);

            this.interactionTimeoutId = setTimeout(() => {
    
                this.submit();
    
                clearTimeout(this.interactionTimeoutId);
                this.interactionTimeoutId = null;

                resolve(true);
    
            }, config.debounceTimeout);
        });

        this.interactionPromise = Object.assign(promise, { reject: refReject });

        return this.interactionPromise;
    }

    changeActiveButtonEvent(e, inputName) {
        
        const
            { target } = e,
            activeClass = style('active'),
            input = this.nodes.form.querySelector(`[name="${inputName}"]`);
        
        if (input) {

            e.target.parentNode
                .querySelectorAll(`[data-role="tag"].${activeClass}`)
                .forEach((el) => el.classList.remove(activeClass));

            input.value = target.innerText;
            input.dispatchEvent(new Event('change'));
            input.addEventListener('input', () => e.target.classList.remove(activeClass), { once: true });

            target.classList.add(activeClass);
        }
        else {

            throw new Error(`Can't find input with name ${inputName}`);
        }
    }

    amortizationEvent() {

        this.nodes.amortization?.show();
    }

    submitEvent(e) {

        e.preventDefault();

        this.submit();
    }

    applicationClickEvent(button) {

        if (button.link)
            window.open(button.link, '_blank');
    }

    changeRangeEvent(e) {

        this.state.price.value = NumberFormatter.parse(e.target.value);
        this.setState({ price: this.state.price });
    }
    
}